import { useState, useMemo } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEO } from '@/components/shared/SEO';
import {
  Home,
  TrendingUp,
  BarChart,
  FileText,
  Smartphone,
  Palette,
  Calendar,
  Clock,
  User,
  Eye,
  Heart,
  Search,
  Tag,
  ChevronRight,
  Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageSkeleton } from '@/components/ui/Skeleton';
import { useBlogPosts } from '@/hooks/useBlog';
import { useTenant } from '@/hooks/useTenant';

// Mapeo de iconos
const iconMap: Record<string, any> = {
  'Home': Home,
  'TrendingUp': TrendingUp,
  'BarChart': BarChart,
  'FileText': FileText,
  'Smartphone': Smartphone,
  'Palette': Palette
};

// Mapeo de categorías a iconos y colores
const categoryConfig: Record<string, { icon: string; color: string }> = {
  'Guías': { icon: 'Home', color: '#7c3aed' },
  'Mercado': { icon: 'TrendingUp', color: '#06b6d4' },
  'Inversión': { icon: 'BarChart', color: '#10b981' },
  'Consejos': { icon: 'FileText', color: '#f59e0b' },
  'Tecnología': { icon: 'Smartphone', color: '#3b82f6' },
  'Diseño': { icon: 'Palette', color: '#ec4899' }
};

export default function BlogPage() {
  const navigate = useNavigate();
  const { config } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();

  // Cargar posts desde el backend
  const { posts: allPosts, loading, error } = useBlogPosts();

  // Estados para filtros y búsqueda
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));

  // Actualizar URL cuando cambian los filtros
  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  };

  // Filtrar y paginar posts
  const filteredPosts = useMemo(() => {
    if (!allPosts) return [];

    let filtered = allPosts;

    // Filtrar por búsqueda
    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filtrar por categoría
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    return filtered;
  }, [allPosts, searchQuery, selectedCategory]);

  const postsPerPage = 6;
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const currentPosts = filteredPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  // Extraer categorías únicas de los posts
  const categories = useMemo(() => {
    if (!allPosts) return [];
    const uniqueCategories = Array.from(new Set(allPosts.map(post => post.category)));
    return uniqueCategories;
  }, [allPosts]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    updateSearchParams();
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
    updateSearchParams();
  };

  const handleHeroSearch = (query: string) => {
    handleSearch(query);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Reciente';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryConfig = (categoryName: string) => {
    return categoryConfig[categoryName] || { icon: 'Home', color: '#7c3aed' };
  };

  // Generate URLs and data
  const currentUrl = `${window.location.origin}/blog`;

  // JSON-LD Structured Data
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": `Blog Inmobiliario | ${config?.name || 'Inmobiliaria'}`,
        "description": "Blog con artículos especializados en bienes raíces",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": window.location.origin
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": currentUrl
            }
          ]
        }
      }
    ]
  };

  // SEO dinámico
  const seoTitle = `Blog Inmobiliario | ${config?.name || 'Inmobiliaria'}`;
  const seoDescription = "Lee consejos, tendencias y noticias sobre el mercado inmobiliario. Guías para compradores, vendedores e inversionistas. ¡Información actualizada!";
  const seoKeywords = ["blog inmobiliario", "consejos compra vivienda", "mercado inmobiliario", "noticias bienes raíces", "guía inversión inmobiliaria"];

  if (loading || !config) {
    return <PageSkeleton message="Cargando blog..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Error al cargar el blog
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Intentar de nuevo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Blog"
        description={seoDescription}
        keywords={seoKeywords}
        canonicalPath="/blog"
        ogImage="/images/hero/blog-hero.jpg"
      />

      <Helmet>
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <PageLayout
        hero={{
          title: "Blog Inmobiliario",
          subtitle: "Consejos, tendencias y noticias del sector inmobiliario",
          backgroundImage: "/images/hero/blog-hero.jpg",
          showSearch: true,
          searchPlaceholder: "Buscar artículos...",
          onSearch: handleHeroSearch
        }}
      >
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {/* Search and Filters */}
              <div className="mb-8">
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Buscar artículos..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* Categories */}
                {categories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    <Button
                      variant={selectedCategory === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleCategoryChange('all')}
                    >
                      Todas
                    </Button>
                    {categories.map((category) => {
                      const catConfig = getCategoryConfig(category);
                      const IconComponent = iconMap[catConfig.icon] || Home;
                      return (
                        <Button
                          key={category}
                          variant={selectedCategory === category ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleCategoryChange(category)}
                          className="flex items-center gap-2"
                        >
                          <IconComponent className="h-4 w-4" />
                          {category}
                        </Button>
                      );
                    })}
                  </div>
                )}

                {/* Results Info */}
                <div className="text-gray-600 mb-6">
                  {filteredPosts.length > 0 ? (
                    <span>
                      Mostrando {currentPosts.length} de {filteredPosts.length} artículos
                      {searchQuery && ` para "${searchQuery}"`}
                      {selectedCategory !== 'all' && ` en ${selectedCategory}`}
                    </span>
                  ) : (
                    <span>No se encontraron artículos que coincidan con tu búsqueda</span>
                  )}
                </div>
              </div>

              {/* Posts Grid */}
              {currentPosts.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-8 mb-12">
                  {currentPosts.map((post) => {
                    const catConfig = getCategoryConfig(post.category);
                    const IconComponent = iconMap[catConfig.icon] || Home;

                    return (
                      <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
                        <div className="relative">
                          <img
                            src={post.featuredImage || '/placeholder-image.jpg'}
                            alt={post.title}
                            className="w-full h-48 object-cover"
                          />
                          {post.featured && (
                            <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold">
                              Destacado
                            </div>
                          )}
                        </div>

                        <div className="p-6">
                          <div className="flex items-center mb-3">
                            <span
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                              style={{ backgroundColor: catConfig.color }}
                            >
                              <IconComponent className="h-3 w-3 mr-1" />
                              {post.category}
                            </span>
                          </div>

                          <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                            {post.title}
                          </h2>

                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {formatDate(post.publishedAt)}
                              </div>
                              {post.readTime && (
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {post.readTime} min
                                </div>
                              )}
                            </div>

                            <div className="flex items-center space-x-3">
                              <div className="flex items-center">
                                <Eye className="h-4 w-4 mr-1" />
                                {post.views}
                              </div>
                              <div className="flex items-center">
                                <Heart className="h-4 w-4 mr-1" />
                                {post.likes}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <img
                                src={post.author.avatar}
                                alt={post.author.name}
                                className="w-8 h-8 rounded-full mr-3"
                              />
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">{post.author.name}</p>
                                <p className="text-gray-500 text-xs">{post.author.bio}</p>
                              </div>
                            </div>

                            <Button
                              size="sm"
                              onClick={() => navigate(`/blog/${post.slug}`)}
                            >
                              Leer más
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 mb-4">
                    <Search className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">No se encontraron artículos</h3>
                    <p>Intenta ajustar tu búsqueda o explorar otras categorías</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setCurrentPage(1);
                    }}
                  >
                    Ver todos los artículos
                  </Button>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center space-x-2">
                  <Button
                    variant="outline"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Anterior
                  </Button>

                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    );
                  })}

                  <Button
                    variant="outline"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:w-80">
              <div className="space-y-8">
                {/* Featured Posts Widget */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Artículos Destacados</h3>
                  <div className="space-y-4">
                    {allPosts
                      ?.filter(post => post.featured)
                      ?.slice(0, 3)
                      ?.map((post) => (
                      <div key={post.id} className="flex space-x-3 cursor-pointer" onClick={() => navigate(`/blog/${post.slug}`)}>
                        <img
                          src={post.featuredImage || '/placeholder-image.jpg'}
                          alt={post.title}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-grow">
                          <h4 className="font-semibold text-sm line-clamp-2 mb-1">
                            {post.title}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {formatDate(post.publishedAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Categories Widget */}
                {categories.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Categorías</h3>
                    <div className="space-y-2">
                      {categories.map((category) => {
                        const count = allPosts?.filter(post => post.category === category).length || 0;
                        const catConfig = getCategoryConfig(category);
                        const IconComponent = iconMap[catConfig.icon] || Home;

                        return (
                          <div
                            key={category}
                            className="flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleCategoryChange(category)}
                          >
                            <div className="flex items-center">
                              <IconComponent className="h-4 w-4 mr-2" style={{ color: catConfig.color }} />
                              <span className="text-sm">{category}</span>
                            </div>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Popular Tags Widget */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Tags Populares</h3>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(new Set(allPosts?.flatMap(post => post.tags) || []))
                      ?.slice(0, 10)
                      ?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-primary hover:text-white cursor-pointer transition-colors"
                        onClick={() => handleSearch(tag)}
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Newsletter Widget */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Suscríbete al Newsletter</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Recibe las últimas noticias y artículos directamente en tu correo
                  </p>
                  <div className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Tu email"
                      className="w-full"
                    />
                    <Button className="w-full">
                      <Mail className="h-4 w-4 mr-2" />
                      Suscribirse
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
